import { AuthenticatedUser, MapCompositeType, MapIcon } from '@gettactic/api';
import React, { useState, useMemo, useCallback } from 'react';
import semver from 'semver';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import {
  CompositeTemplate,
  MapCurrentPosition,
  MapInteractiveZoneActionOptions,
  MapInteractiveZones,
  Shape,
  SpotShape,
  SpotShape001,
  ZoneTypeDefinition
} from '../../../../types';
import { migrateWallsToPath } from '../../../../utils/wallPathMigration';
import { bakeWallRotations } from '../../../../utils/wallRotationMigration';
import { MAP_BUILDER_VERSION } from '../../../../version';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { SpotLabelPatternItem, StagePosition } from '../../types';
import { MapCanvasState, MapCanvasValue } from './MapCanvasContext.types';
import { MapCanvasContext } from './useMapCanvas';
import { MapCanvasStageContext } from './useMapCanvasStage';
import { MapCanvasStagePositionContext } from './useMapCanvasStagePosition';
import { MapCanvasShapesContext } from './useMapCanvasShapes';
import { MapCanvasInteractionContext } from './useMapCanvasInteraction';

export interface MapCanvasProviderProps {
  value?: MapCanvasValue;
  isViewMode?: boolean;
  initialPosition?: MapCurrentPosition;
  interactiveZones?: MapInteractiveZones;
  highlightResource?: (
    resourceId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
  onZoneLinkClick?: MapCanvasState['onZoneLinkClick'];
  isPrivacyMode?: boolean;
  authenticatedUser?: AuthenticatedUser;
  isDark?: boolean;
  teamColors?: Record<string, string>;
  /** Per-resource fill override (heatmaps): resource id -> CSS colour. Wins over
   *  every other colour source and also colours the availability dot. */
  resourceColors?: Record<string, string>;
  /** Per-zone fill override (campus heatmaps): zone id -> CSS colour. */
  zoneColors?: Record<string, string>;
  resourceId?: string;
  fetchMapVersions?: MapCanvasState['fetchMapVersions'];
  activateMapVersion?: MapCanvasState['activateMapVersion'];
  onUploadIcon?: MapCanvasState['onUploadIcon'];
  canRebuildWithAi?: boolean;
  mapIcons?: MapIcon[];
  apiCompositeTypes?: MapCompositeType[];
  saveCompositeType?: MapCanvasState['saveCompositeType'];
  updateCompositeType?: MapCanvasState['updateCompositeType'];
  deleteCompositeType?: MapCanvasState['deleteCompositeType'];
}

const minScale = 0.1;
const maxScale = 10;

export const MapCanvasProvider: React.FC<
  React.PropsWithChildren<MapCanvasProviderProps>
> = ({
  children,
  value,
  initialPosition,
  interactiveZones,
  highlightResource,
  onZoneLinkClick,
  isViewMode = false,
  isPrivacyMode = false,
  authenticatedUser,
  isDark: isDarkProp = false,
  teamColors,
  resourceColors,
  zoneColors,
  resourceId,
  fetchMapVersions,
  activateMapVersion,
  onUploadIcon,
  canRebuildWithAi,
  mapIcons,
  apiCompositeTypes,
  saveCompositeType,
  updateCompositeType,
  deleteCompositeType
}) => {
  const version = MAP_BUILDER_VERSION;
  const { width, height } = useWindowSize();
  const [stagePosition, setStagePosition] = React.useState<StagePosition>({
    x: initialPosition ? initialPosition.x || 0 : 0,
    y: initialPosition ? initialPosition.y || 0 : 0,
    scale: initialPosition ? initialPosition.scaleX || 1 : 1
  });
  const [stageScale, setStageScale] = React.useState(
    initialPosition ? initialPosition.scaleX || 1 : 1
  );
  const [lastSpotLabel, setLastSpotLabel] = useState<string[] | null>([
    'A',
    '0'
  ]);
  const [spotLabelPattern, setSpotLabelPattern] = useState<
    SpotLabelPatternItem[]
  >([
    { type: 'text', value: '' },
    { type: 'numbers', depth: 9999 }
  ]);
  const [isDark] = useState(isDarkProp);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [compositeTemplates, setCompositeTemplates] = useState<
    CompositeTemplate[]
  >([]);
  const [zoneTypes, setZoneTypes] = useState<ZoneTypeDefinition[]>([]);
  const [pixelsPerMeter, setPixelsPerMeter] = useState<number | null>(null);
  const [gridSize, setGridSize] = React.useState(1);
  const [editingZoneWidth, setEditingZoneWidth] = React.useState(2200);
  const [editingZoneHeight, setEditingZoneHeight] = React.useState(1800);
  const [minSelectionDistance] = React.useState(5);
  const hoveringNodeRef = React.useRef<SpotShape | null>(null);
  const setHoveringNode = React.useCallback((node: SpotShape | null) => {
    hoveringNodeRef.current = node;
  }, []);
  const [selectionBox, setSelectionBox] = React.useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [selectionStart, setSelectionStart] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [shapesInSelection, setShapesInSelection] = React.useState<string[]>(
    []
  );
  const [isAltPressed, setIsAltPressed] = React.useState(false);
  const [dragStartPositions, setDragStartPositions] = React.useState<{
    [key: string]: { x: number; y: number };
  }>({});

  // Stable refs for frequently-changing values
  const shapesRef = React.useRef(shapes);
  shapesRef.current = shapes;
  const stagePositionRef = React.useRef(stagePosition);
  stagePositionRef.current = stagePosition;

  React.useEffect(() => {
    window.document.body.classList.remove('dark');
    if (isDark) window.document.body.classList.add('dark');
  }, [isDark]);

  const prevValueRef = React.useRef<MapCanvasValue | undefined>(undefined);

  React.useEffect(() => {
    if (value && value !== prevValueRef.current) {
      // In the EDITOR, only the first value loads the canvas. Later
      // identity changes come from background refetches (e.g. tabbing
      // back re-fetches resources and re-stamps labels onto the map
      // value) — reloading would wipe unsaved work on the canvas.
      // View mode keeps syncing so hosts can refresh the map in place.
      if (!isViewMode && prevValueRef.current !== undefined) {
        prevValueRef.current = value;
        return;
      }
      prevValueRef.current = value;

      let loadedShapes = value.shapes.map((shape) => {
        if (!value.version) {
          if (shape.type === 'spot') {
            const oldShape = shape as unknown as SpotShape001;
            return {
              ...oldShape,
              ...oldShape.rows[0].items[0],
              rows: undefined,
              label: oldShape.rows[0].items[0].textProps?.text || ''
            } as SpotShape;
          }
        }

        return shape;
      });

      // Migrate legacy wall segments to wall-path shapes
      loadedShapes = migrateWallsToPath(loadedShapes);
      loadedShapes = bakeWallRotations(loadedShapes);

      setShapes(loadedShapes);
      setGridSize(value.gridSize);
      updateEditingZoneSize(value.editingZoneWidth, value.editingZoneHeight);
      if (value.lastSpotLabel && value.version)
        setLastSpotLabel(value.lastSpotLabel);
      if (value.spotLabelPattern && value.version)
        setSpotLabelPattern(value.spotLabelPattern);
      if (value.compositeTemplates)
        setCompositeTemplates(value.compositeTemplates);
      if (value.zoneTypes) setZoneTypes(value.zoneTypes);
      setPixelsPerMeter(value.pixelsPerMeter ?? null);
    }
  }, [value]);

  const loadMapValue = React.useCallback(
    (mapValue: MapCanvasValue) => {
      let loadedShapes = mapValue.shapes.map((shape) => {
        if (!mapValue.version) {
          if (shape.type === 'spot') {
            const oldShape = shape as unknown as SpotShape001;
            return {
              ...oldShape,
              ...oldShape.rows[0].items[0],
              rows: undefined,
              label: oldShape.rows[0].items[0].textProps?.text || ''
            } as SpotShape;
          }
        }
        return shape;
      });
      loadedShapes = migrateWallsToPath(loadedShapes);
      loadedShapes = bakeWallRotations(loadedShapes);
      setShapes(loadedShapes);
      setGridSize(mapValue.gridSize);
      updateEditingZoneSize(mapValue.editingZoneWidth, mapValue.editingZoneHeight);
      if (mapValue.lastSpotLabel && mapValue.version)
        setLastSpotLabel(mapValue.lastSpotLabel);
      if (mapValue.spotLabelPattern && mapValue.version)
        setSpotLabelPattern(mapValue.spotLabelPattern);
      if (mapValue.compositeTemplates)
        setCompositeTemplates(mapValue.compositeTemplates);
      if (mapValue.zoneTypes) setZoneTypes(mapValue.zoneTypes);
      setPixelsPerMeter(mapValue.pixelsPerMeter ?? null);
    },
    []
  );

  const updateStagePosition = React.useCallback(
    (position: Partial<StagePosition>) => {
      setStagePosition((prev) => {
        const next = { ...prev, ...position };
        if (position.scale !== undefined && position.scale !== prev.scale) {
          setStageScale(position.scale);
        }
        return next;
      });
    },
    []
  );

  const updateEditingZoneSize = React.useCallback(
    (width: number, height: number) => {
      setEditingZoneWidth(width);
      setEditingZoneHeight(height);
    },
    []
  );

  const centerStage = React.useCallback(():
    | { x: number; y: number; scale: number }
    | undefined => {
    const konva = getWindowKonvaObject();
    const stage = konva?.stages?.[0];
    if (!stage) return undefined;

    // The stage canvas is window-sized but can sit inside a smaller,
    // clipped area (the editor flexes side panels next to it). Center and
    // fit against the visible portion of the container, not the window.
    const rect = stage.container().getBoundingClientRect();
    const visibleLeft = Math.max(rect.left, 0);
    const visibleTop = Math.max(rect.top, 0);
    const visibleRight = Math.min(rect.right, window.innerWidth);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    const visibleWidth = visibleRight - visibleLeft;
    const visibleHeight = visibleBottom - visibleTop;
    const fitWidth = visibleWidth > 0 ? visibleWidth : width;
    const fitHeight = visibleHeight > 0 ? visibleHeight : height;
    // Visible-area center in stage-container coordinates.
    const visibleCenterX =
      visibleWidth > 0 ? visibleLeft + visibleWidth / 2 - rect.left : width / 2;
    const visibleCenterY =
      visibleHeight > 0
        ? visibleTop + visibleHeight / 2 - rect.top
        : height / 2;

    // Small margin so the map doesn't touch the panels/edges.
    const FIT_PADDING = 0.95;
    const scale =
      Math.min(fitWidth / editingZoneWidth, fitHeight / editingZoneHeight) *
      FIT_PADDING;
    stage.scale({ x: scale, y: scale });

    const editingZoneCenter = {
      x: editingZoneWidth / 2,
      y: editingZoneHeight / 2
    };

    const newStagePos = {
      x: visibleCenterX - editingZoneCenter.x * scale,
      y: visibleCenterY - editingZoneCenter.y * scale
    };

    setStagePosition({
      x: newStagePos.x,
      y: newStagePos.y,
      scale: scale
    });
    setStageScale(scale);

    stage.position(newStagePos);
    stage.batchDraw();

    return { x: newStagePos.x, y: newStagePos.y, scale };
  }, [width, height, editingZoneWidth, editingZoneHeight]);

  // Use shapesRef so getCanvasValue doesn't depend on shapes state
  const getCanvasValue = useCallback((): MapCanvasValue => {
    return {
      version,
      lastUpdate: new Date().toISOString(),
      shapes: shapesRef.current,
      gridSize,
      editingZoneWidth,
      editingZoneHeight,
      spotLabelPattern,
      lastSpotLabel,
      compositeTemplates:
        compositeTemplates.length > 0
          ? compositeTemplates
          : undefined,
      zoneTypes: zoneTypes.length > 0 ? zoneTypes : undefined,
      pixelsPerMeter: pixelsPerMeter ?? undefined
    };
  }, [
    version,
    gridSize,
    editingZoneWidth,
    editingZoneHeight,
    spotLabelPattern,
    lastSpotLabel,
    compositeTemplates,
    zoneTypes,
    pixelsPerMeter
  ]);

  // Stage position context — changes on every pan/zoom frame
  const stagePositionValue = useMemo(
    () => ({ stagePosition }),
    [stagePosition]
  );

  // Stage context — changes only on zoom (not on pan)
  const stageValue = useMemo(
    () => ({
      stagePositionRef,
      stageScale,
      setStagePosition,
      updateStagePosition,
      centerStage,
      minScale,
      maxScale
    }),
    [stageScale, updateStagePosition, centerStage]
  );

  // Shapes context — changes when shapes are added/removed/modified
  const shapesValue = useMemo(
    () => ({ shapes }),
    [shapes]
  );

  // Interaction context — changes during selection/drag
  const interactionValue = useMemo(
    () => ({
      selectionBox,
      setSelectionBox,
      selectionStart,
      setSelectionStart,
      isDragging,
      setIsDragging,
      shapesInSelection,
      setShapesInSelection,
      isAltPressed,
      setIsAltPressed,
      dragStartPositions,
      setDragStartPositions,
      minSelectionDistance
    }),
    [
      selectionBox,
      selectionStart,
      isDragging,
      shapesInSelection,
      isAltPressed,
      dragStartPositions,
      minSelectionDistance
    ]
  );

  // Core context — changes rarely (grid, editing zone, config, etc.)
  const coreValue: MapCanvasState = useMemo(
    () => ({
      version,
      gridSize,
      editingZoneWidth,
      editingZoneHeight,
      setGridSize,
      shapesRef,
      setShapes,
      isDark,
      lastSpotLabel,
      setLastSpotLabel,
      spotLabelPattern,
      setSpotLabelPattern,
      compositeTemplates,
      setCompositeTemplates,
      zoneTypes,
      setZoneTypes,
      pixelsPerMeter,
      setPixelsPerMeter,
      getCanvasValue,
      width,
      height,
      isViewMode,
      updateEditingZoneSize,
      interactiveZones,
      highlightResource,
      onZoneLinkClick,
      isPrivacyMode,
      authenticatedUser,
      setHoveringNode,
      teamColors,
      resourceColors,
      zoneColors,
      resourceId,
      loadMapValue,
      fetchMapVersions,
      activateMapVersion,
      onUploadIcon,
      canRebuildWithAi,
      mapIcons,
      apiCompositeTypes,
      saveCompositeType,
      updateCompositeType,
      deleteCompositeType
    }),
    [
      version,
      gridSize,
      editingZoneWidth,
      editingZoneHeight,
      isDark,
      lastSpotLabel,
      spotLabelPattern,
      compositeTemplates,
      zoneTypes,
      pixelsPerMeter,
      getCanvasValue,
      width,
      height,
      isViewMode,
      updateEditingZoneSize,
      interactiveZones,
      highlightResource,
      onZoneLinkClick,
      isPrivacyMode,
      authenticatedUser,
      setHoveringNode,
      teamColors,
      resourceColors,
      zoneColors,
      resourceId,
      loadMapValue,
      fetchMapVersions,
      activateMapVersion,
      onUploadIcon,
      canRebuildWithAi,
      mapIcons,
      apiCompositeTypes,
      saveCompositeType,
      updateCompositeType,
      deleteCompositeType
    ]
  );

  return (
    <MapCanvasStagePositionContext.Provider value={stagePositionValue}>
      <MapCanvasStageContext.Provider value={stageValue}>
        <MapCanvasShapesContext.Provider value={shapesValue}>
          <MapCanvasInteractionContext.Provider value={interactionValue}>
            <MapCanvasContext.Provider value={coreValue}>
              {children}
            </MapCanvasContext.Provider>
          </MapCanvasInteractionContext.Provider>
        </MapCanvasShapesContext.Provider>
      </MapCanvasStageContext.Provider>
    </MapCanvasStagePositionContext.Provider>
  );
};
